// src/models/inventoryModels.js
// ─────────────────────────────────────────────────────────────────────────────

const base = (d) => ({
  id:        d.id || d._id || null,
  companyId: d.companyId || null,
  isActive:  d.isActive !== undefined ? d.isActive : true,
  createdAt: d.createdAt || null,
  updatedAt: d.updatedAt || null,
  createdBy: d.createdBy || null,
});

// ── 1. CATEGORY ───────────────────────────────────────────────────────────────
export class Category {
  constructor(d = {}) {
    Object.assign(this, base(d));
    this.name        = d.name        || '';
    this.code        = d.code        || '';
    this.description = d.description || '';
    this.image       = d.image       || null;
    this.sortOrder   = d.sortOrder   || 0;
    this.status      = d.status      || 'Active';
  }
  validate() {
    const e = [];
    if (!this.name?.trim()) e.push('Category name is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Category(d) : null; }
}

// ── 2. SUB CATEGORY ───────────────────────────────────────────────────────────
export class SubCategory {
  constructor(d = {}) {
    Object.assign(this, base(d));
    this.name        = d.name        || '';
    this.code        = d.code        || '';
    this.categoryId  = d.categoryId  || null;
    this.categoryName= d.categoryName|| '';
    this.description = d.description || '';
    this.image       = d.image       || null;
    this.sortOrder   = d.sortOrder   || 0;
    this.status      = d.status      || 'Active';
  }
  validate() {
    const e = [];
    if (!this.name?.trim())   e.push('Sub-category name is required');
    if (!this.categoryId)     e.push('Parent category is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new SubCategory(d) : null; }
}

// ── 3. BRAND ──────────────────────────────────────────────────────────────────
export class Brand {
  constructor(d = {}) {
    Object.assign(this, base(d));
    this.name        = d.name        || '';
    this.code        = d.code        || '';
    this.description = d.description || '';
    this.logo        = d.logo        || null;
    this.website     = d.website     || '';
    this.country     = d.country     || '';
    this.status      = d.status      || 'Active';
  }
  validate() {
    const e = [];
    if (!this.name?.trim()) e.push('Brand name is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Brand(d) : null; }
}

// ── 4. UNIT OF MEASURE ────────────────────────────────────────────────────────
export class Unit {
  constructor(d = {}) {
    Object.assign(this, base(d));
    this.name         = d.name         || '';
    this.shortName    = d.shortName    || '';
    this.type         = d.type         || 'Quantity';
    this.baseUnit     = d.baseUnit     || '';
    this.conversionFactor = d.conversionFactor || 1;
    this.status       = d.status       || 'Active';
  }
  validate() {
    const e = [];
    if (!this.name?.trim())      e.push('Unit name is required');
    if (!this.shortName?.trim()) e.push('Short name is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Unit(d) : null; }
}

// ── 5. PRODUCT ────────────────────────────────────────────────────────────────
export class Product {
  constructor(d = {}) {
    Object.assign(this, base(d));
    this.name           = d.name           || '';
    this.sku            = d.sku            || '';
    this.barcode        = d.barcode        || '';
    this.qrCode         = d.qrCode         || '';
    this.categoryId     = d.categoryId     || null;
    this.categoryName   = d.categoryName   || '';
    this.subCategoryId  = d.subCategoryId  || null;
    this.subCategoryName= d.subCategoryName|| '';
    this.brandId        = d.brandId        || null;
    this.brandName      = d.brandName      || '';
    this.unitId         = d.unitId         || null;
    this.unitName       = d.unitName       || '';
    this.description    = d.description    || '';
    this.specifications = d.specifications || '';
    this.purchasePrice  = d.purchasePrice  || 0;
    this.sellingPrice   = d.sellingPrice   || 0;
    this.mrp            = d.mrp            || 0;
    this.taxRate        = d.taxRate        || 0;
    this.hsnCode        = d.hsnCode        || '';
    this.minStock       = d.minStock       || 0;
    this.maxStock       = d.maxStock       || 0;
    this.reorderLevel   = d.reorderLevel   || 0;
    this.reorderQty     = d.reorderQty     || 0;
    this.currentStock   = d.currentStock   || 0;
    this.reservedStock  = d.reservedStock  || 0;
    this.warehouseId    = d.warehouseId    || null;
    this.warehouseName  = d.warehouseName  || '';
    this.locationId     = d.locationId     || null;
    this.locationName   = d.locationName   || '';
    this.images         = d.images         || [];
    this.weight         = d.weight         || '';
    this.dimensions     = d.dimensions     || '';
    this.expiryDate     = d.expiryDate     || null;
    this.batchTracking  = d.batchTracking  || false;
    this.serialTracking = d.serialTracking || false;
    this.status         = d.status         || 'Active';
    this.tags           = d.tags           || [];
    this.notes          = d.notes          || '';
  }
  get availableStock() { return this.currentStock - this.reservedStock; }
  get stockStatus() {
    if (this.currentStock <= 0) return 'Out of Stock';
    if (this.currentStock <= this.minStock) return 'Low Stock';
    return 'In Stock';
  }
  validate() {
    const e = [];
    if (!this.name?.trim())     e.push('Product name is required');
    if (!this.sku?.trim())      e.push('SKU is required');
    if (!this.categoryId)       e.push('Category is required');
    if (this.sellingPrice < 0)  e.push('Selling price cannot be negative');
    if (this.purchasePrice < 0) e.push('Purchase price cannot be negative');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Product(d) : null; }
}

// ── 6. WAREHOUSE ──────────────────────────────────────────────────────────────
export class Warehouse {
  constructor(d = {}) {
    Object.assign(this, base(d));
    this.name        = d.name        || '';
    this.code        = d.code        || '';
    this.address     = d.address     || '';
    this.city        = d.city        || '';
    this.state       = d.state       || '';
    this.pincode     = d.pincode     || '';
    this.phone       = d.phone       || '';
    this.email       = d.email       || '';
    this.managerName = d.managerName || '';
    this.managerPhone= d.managerPhone|| '';
    this.capacity    = d.capacity    || 0;
    this.usedCapacity= d.usedCapacity|| 0;
    this.type        = d.type        || 'Main';
    this.status      = d.status      || 'Active';
    this.notes       = d.notes       || '';
  }
  get capacityPercent() { return this.capacity ? Math.round((this.usedCapacity/this.capacity)*100) : 0; }
  validate() {
    const e = [];
    if (!this.name?.trim()) e.push('Warehouse name is required');
    if (!this.code?.trim()) e.push('Warehouse code is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Warehouse(d) : null; }
}

// ── 7. STORAGE LOCATION ───────────────────────────────────────────────────────
export class Location {
  constructor(d = {}) {
    Object.assign(this, base(d));
    this.name         = d.name         || '';
    this.code         = d.code         || '';
    this.warehouseId  = d.warehouseId  || null;
    this.warehouseName= d.warehouseName|| '';
    this.zone         = d.zone         || '';
    this.aisle        = d.aisle        || '';
    this.rack         = d.rack         || '';
    this.shelf        = d.shelf        || '';
    this.bin          = d.bin          || '';
    this.capacity     = d.capacity     || 0;
    this.usedCapacity = d.usedCapacity || 0;
    this.type         = d.type         || 'Shelf';
    this.status       = d.status       || 'Active';
  }
  get fullPath() { return [this.zone, this.aisle, this.rack, this.shelf, this.bin].filter(Boolean).join('-'); }
  validate() {
    const e = [];
    if (!this.name?.trim())    e.push('Location name is required');
    if (!this.warehouseId)     e.push('Warehouse is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Location(d) : null; }
}

// ── 8. SUPPLIER ───────────────────────────────────────────────────────────────
export class Supplier {
  constructor(d = {}) {
    Object.assign(this, base(d));
    this.name           = d.name           || '';
    this.code           = d.code           || '';
    this.contactPerson  = d.contactPerson  || '';
    this.phone          = d.phone          || '';
    this.email          = d.email          || '';
    this.address        = d.address        || '';
    this.city           = d.city           || '';
    this.state          = d.state          || '';
    this.pincode        = d.pincode        || '';
    this.gstin          = d.gstin          || '';
    this.pan            = d.pan            || '';
    this.bankName       = d.bankName       || '';
    this.accountNumber  = d.accountNumber  || '';
    this.ifscCode       = d.ifscCode       || '';
    this.paymentTerms   = d.paymentTerms   || 'Net 30';
    this.creditLimit    = d.creditLimit    || 0;
    this.outstanding    = d.outstanding    || 0;
    this.rating         = d.rating         || 0;
    this.status         = d.status         || 'Active';
    this.notes          = d.notes          || '';
  }
  validate() {
    const e = [];
    if (!this.name?.trim())  e.push('Supplier name is required');
    if (!this.phone?.trim()) e.push('Phone is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Supplier(d) : null; }
}

// ── 9. PURCHASE ORDER ─────────────────────────────────────────────────────────
export class PurchaseOrder {
  constructor(d = {}) {
    Object.assign(this, base(d));
    this.poNumber       = d.poNumber       || '';
    this.supplierId     = d.supplierId     || null;
    this.supplierName   = d.supplierName   || '';
    this.warehouseId    = d.warehouseId    || null;
    this.warehouseName  = d.warehouseName  || '';
    this.orderDate      = d.orderDate      || '';
    this.expectedDate   = d.expectedDate   || '';
    this.receivedDate   = d.receivedDate   || null;
    this.items          = d.items          || [];
    this.subtotal       = d.subtotal       || 0;
    this.taxAmount      = d.taxAmount      || 0;
    this.discount       = d.discount       || 0;
    this.shippingCharge = d.shippingCharge || 0;
    this.totalAmount    = d.totalAmount    || 0;
    this.paidAmount     = d.paidAmount     || 0;
    this.paymentMethod  = d.paymentMethod  || '';
    this.paymentStatus  = d.paymentStatus  || 'Unpaid';
    this.status         = d.status         || 'Draft';
    this.notes          = d.notes          || '';
    this.terms          = d.terms          || '';
  }
  get balance() { return this.totalAmount - this.paidAmount; }
  validate() {
    const e = [];
    if (!this.supplierId)    e.push('Supplier is required');
    if (!this.orderDate)     e.push('Order date is required');
    if (!this.items?.length) e.push('At least one item is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new PurchaseOrder(d) : null; }
}

// ── 10. PURCHASE RETURN ───────────────────────────────────────────────────────
export class PurchaseReturn {
  constructor(d = {}) {
    Object.assign(this, base(d));
    this.returnNumber  = d.returnNumber  || '';
    this.poId          = d.poId          || null;
    this.poNumber      = d.poNumber      || '';
    this.supplierId    = d.supplierId    || null;
    this.supplierName  = d.supplierName  || '';
    this.returnDate    = d.returnDate    || '';
    this.items         = d.items         || [];
    this.totalAmount   = d.totalAmount   || 0;
    this.reason        = d.reason        || '';
    this.status        = d.status        || 'Pending';
    this.notes         = d.notes         || '';
  }
  validate() {
    const e = [];
    if (!this.supplierId)    e.push('Supplier is required');
    if (!this.returnDate)    e.push('Return date is required');
    if (!this.items?.length) e.push('At least one item is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new PurchaseReturn(d) : null; }
}

// ── 11. STOCK IN ──────────────────────────────────────────────────────────────
export class StockIn {
  constructor(d = {}) {
    Object.assign(this, base(d));
    this.referenceNumber = d.referenceNumber || '';
    this.type            = d.type            || 'Purchase';
    this.sourceId        = d.sourceId        || null;
    this.warehouseId     = d.warehouseId     || null;
    this.warehouseName   = d.warehouseName   || '';
    this.locationId      = d.locationId      || null;
    this.locationName    = d.locationName    || '';
    this.date            = d.date            || '';
    this.items           = d.items           || [];
    this.totalQty        = d.totalQty        || 0;
    this.totalAmount     = d.totalAmount     || 0;
    this.status          = d.status          || 'Completed';
    this.notes           = d.notes           || '';
    this.receivedBy      = d.receivedBy      || '';
  }
  validate() {
    const e = [];
    if (!this.warehouseId)   e.push('Warehouse is required');
    if (!this.date)          e.push('Date is required');
    if (!this.items?.length) e.push('At least one item is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new StockIn(d) : null; }
}

// ── 12. STOCK OUT ─────────────────────────────────────────────────────────────
export class StockOut {
  constructor(d = {}) {
    Object.assign(this, base(d));
    this.referenceNumber = d.referenceNumber || '';
    this.type            = d.type            || 'Sale';
    this.warehouseId     = d.warehouseId     || null;
    this.warehouseName   = d.warehouseName   || '';
    this.date            = d.date            || '';
    this.items           = d.items           || [];
    this.totalQty        = d.totalQty        || 0;
    this.totalAmount     = d.totalAmount     || 0;
    this.issuedTo        = d.issuedTo        || '';
    this.status          = d.status          || 'Completed';
    this.notes           = d.notes           || '';
    this.issuedBy        = d.issuedBy        || '';
  }
  validate() {
    const e = [];
    if (!this.warehouseId)   e.push('Warehouse is required');
    if (!this.date)          e.push('Date is required');
    if (!this.items?.length) e.push('At least one item is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new StockOut(d) : null; }
}

// ── 13. STOCK TRANSFER ────────────────────────────────────────────────────────
export class StockTransfer {
  constructor(d = {}) {
    Object.assign(this, base(d));
    this.transferNumber  = d.transferNumber  || '';
    this.fromWarehouseId = d.fromWarehouseId || null;
    this.fromWarehouse   = d.fromWarehouse   || '';
    this.toWarehouseId   = d.toWarehouseId   || null;
    this.toWarehouse     = d.toWarehouse     || '';
    this.fromLocationId  = d.fromLocationId  || null;
    this.toLocationId    = d.toLocationId    || null;
    this.date            = d.date            || '';
    this.items           = d.items           || [];
    this.totalQty        = d.totalQty        || 0;
    this.reason          = d.reason          || '';
    this.status          = d.status          || 'Pending';
    this.notes           = d.notes           || '';
    this.requestedBy     = d.requestedBy     || '';
    this.approvedBy      = d.approvedBy      || '';
  }
  validate() {
    const e = [];
    if (!this.fromWarehouseId) e.push('Source warehouse is required');
    if (!this.toWarehouseId)   e.push('Destination warehouse is required');
    if (this.fromWarehouseId === this.toWarehouseId) e.push('Source and destination cannot be same');
    if (!this.date)            e.push('Transfer date is required');
    if (!this.items?.length)   e.push('At least one item is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new StockTransfer(d) : null; }
}

// ── 14. STOCK ADJUSTMENT ──────────────────────────────────────────────────────
export class StockAdjustment {
  constructor(d = {}) {
    Object.assign(this, base(d));
    this.adjustmentNumber = d.adjustmentNumber || '';
    this.warehouseId      = d.warehouseId      || null;
    this.warehouseName    = d.warehouseName    || '';
    this.date             = d.date             || '';
    this.items            = d.items            || [];
    this.reason           = d.reason           || '';
    this.type             = d.type             || 'Physical Count';
    this.status           = d.status           || 'Draft';
    this.notes            = d.notes            || '';
    this.adjustedBy       = d.adjustedBy       || '';
    this.approvedBy       = d.approvedBy       || '';
  }
  validate() {
    const e = [];
    if (!this.warehouseId)   e.push('Warehouse is required');
    if (!this.date)          e.push('Date is required');
    if (!this.items?.length) e.push('At least one item is required');
    if (!this.reason?.trim()) e.push('Reason is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new StockAdjustment(d) : null; }
}

export default {
  Category, SubCategory, Brand, Unit, Product,
  Warehouse, Location, Supplier,
  PurchaseOrder, PurchaseReturn,
  StockIn, StockOut, StockTransfer, StockAdjustment,
};